import React, { useState, useEffect, ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import * as api from '../services/api';
import {
  ViewIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  AddIcon,
} from '@chakra-ui/icons';
import { FiUsers } from 'react-icons/fi';
import {
  Box,
  Grid,
  GridItem,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  VStack,
  HStack,
  Icon,
  Card,
  CardBody,
  Heading,
  List,
  ListItem,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';

Chart.register(ArcElement, Tooltip, Legend);

const Dashboard: React.FC = () => {
  const quickActions = [
    { label: 'Add Associate', path: '/associates/add', icon: '👤', bg: 'purple.50' },
    { label: 'Add Client', path: '/clients/add', icon: '👥', bg: 'blue.50' },
    { label: 'Add Project', path: '/projects/add', icon: '📂', bg: 'green.50' },
  ];
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [totalClients, setTotalClients] = useState(0);
  const [activeProjects, setActiveProjects] = useState(0);
  const [stats, setStats] = useState({ totalRevenue: 0, growthRate: 0 });
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [unassignedAssociates, setUnassignedAssociates] = useState<any[]>([]);
  const [projectStatusCounts, setProjectStatusCounts] = useState<{ [key: string]: number }>({});
  const [systemAlerts, setSystemAlerts] = useState<any[]>([]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const clientsRes = await api.clientsAPI.getAll();
        setTotalClients(Array.isArray(clientsRes.data) ? clientsRes.data.length : 0);

        const projectsRes = await api.projectsAPI.getAll();
        const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        setActiveProjects(projects.filter((p: any) => p.status === 'active').length);

        const statusCounts: { [key: string]: number } = {};
        projects.forEach((p: any) => {
          statusCounts[p.status] = (statusCounts[p.status] || 0) + 1;
        });
        setProjectStatusCounts(statusCounts);

        const response = await api.dashboardAPI.getDashboardData();
        const { stats, recentActivities, systemAlerts } = response.data;
        setStats(stats);
        setRecentActivities(recentActivities);
        setSystemAlerts(systemAlerts);

        // Fetch unassigned associates
        const unassignedRes = await api.associateAPI.getUnassignedAssociates();
        setUnassignedAssociates(Array.isArray(unassignedRes.data) ? unassignedRes.data : []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    fetchDashboardData();
  }, []);

  const getStatusIcon = (type: string): ElementType => {
    switch (type) {
      case 'project_created':
      case 'milestone':
        return CheckCircleIcon;
      case 'assignment':
      case 'client':
        return ViewIcon;
      default:
        return TimeIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'status.present';
      case 'info': return 'primary.500';
      case 'warning': return 'status.late';
      case 'error': return 'status.absent';
      default: return 'gray.500';
    }
  };

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>
          Dashboard
        </Heading>
      </Box>

      {/* Stats Grid */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
        {/* Total Clients */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <HStack spacing={4} align="center">
                <Box bg="blue.50" rounded="full" p={3}><span className="text-xl">👥</span></Box>
                <Stat>
                  <StatLabel color="gray.600">Total Clients</StatLabel>
                  <StatNumber color="primary.700">{totalClients}</StatNumber>
                </Stat>
              </HStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Active Projects */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <HStack spacing={4} align="center">
                <Box bg="blue.50" rounded="full" p={3}><span className="text-xl">📂</span></Box>
                <Stat>
                  <StatLabel color="gray.600">Active Projects</StatLabel>
                  <StatNumber color="primary.700">{activeProjects}</StatNumber>
                </Stat>
              </HStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Total Revenue */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <HStack spacing={4} align="center">
                <Box bg="blue.50" rounded="full" p={3}><span className="text-xl">💰</span></Box>
                <Stat>
                  <StatLabel color="gray.600">Total Revenue</StatLabel>
                  <StatNumber color="primary.700">${stats.totalRevenue.toLocaleString()}</StatNumber>
                </Stat>
              </HStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Growth Rate */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <HStack spacing={4} align="center">
                <Box bg="green.50" rounded="full" p={3}><span className="text-xl">📈</span></Box>
                <Stat>
                  <StatLabel color="gray.600">Growth Rate</StatLabel>
                  <StatNumber color="primary.700">{stats.growthRate}%</StatNumber>
                </Stat>
              </HStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Main Content */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={8}>
        {/* Left */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Recent Activities */}
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <HStack justify="space-between" mb={4}>
                  <Heading size="md" color="gray.800">Recent Activities</Heading>
                  <Button size="sm" variant="ghost" color="primary.500">View All</Button>
                </HStack>
                <List spacing={3}>
                  {/* Show unassigned associates at the top */}
                  {unassignedAssociates.length > 0 && (
                    <ListItem>
                      <HStack spacing={3} align="flex-start">
                        <Icon as={FiUsers as ElementType} color="purple.500" boxSize={5} />
                        <Box flex={1}>
                          <Text fontSize="sm" color="gray.800" fontWeight="bold">
                            Associates not assigned to any project:
                          </Text>
                          <List pl={4} spacing={2} styleType="disc">
                            {unassignedAssociates.map((a: any) => (
                              <ListItem key={a._id} fontSize="sm" color="gray.700">
                                <Text fontWeight="bold">{a.name}</Text>
                                <Text fontSize="xs" color="gray.500">Email: {a.email || '-'}</Text>
                                <Text fontSize="xs" color="gray.500">Department: {a.department || '-'}</Text>
                                <Text fontSize="xs" color="gray.500">Designation: {a.designation || a.position || '-'}</Text>
                                <Text fontSize="xs" color="gray.500">Status: {a.status || '-'}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </HStack>
                    </ListItem>
                  )}
                  {recentActivities.map((activity: any) => {
                    const IconComponent = getStatusIcon(activity.type);
                    return (
                      <ListItem key={activity.id}>
                        <HStack spacing={3}>
                          <Icon as={IconComponent} color={getStatusColor(activity.status)} boxSize={5} />
                          <Box flex={1}>
                            <Text fontSize="sm" color="gray.800">{activity.message}</Text>
                            <Text fontSize="xs" color="gray.500">{activity.time}</Text>
                          </Box>
                        </HStack>
                      </ListItem>
                    );
                  })}
                </List>
              </CardBody>
            </Card>

            {/* Project Status - Pie */}
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="md" color="gray.800" mb={4}>Project Status Overview</Heading>
                <Box w="300px" h="300px" mx="auto" display="flex" alignItems="center" justifyContent="center">
                  <Pie
                    data={{
                      labels: Object.keys(projectStatusCounts).map(
                        (status) => status.charAt(0).toUpperCase() + status.slice(1)
                      ),
                      datasets: [{
                        data: Object.values(projectStatusCounts),
                        backgroundColor: ['#3182ce', '#38a169', '#e53e3e', '#d69e2e', '#805ad5', '#319795', '#ed8936'],
                        borderColor: '#fff',
                        borderWidth: 2,
                      }],
                    }}
                    options={{
                      plugins: { legend: { display: true, position: 'right' } },
                    }}
                  />
                </Box>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>

        {/* Right */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Quick Actions */}
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="md" color="gray.800" mb={4}>Quick Actions</Heading>
                <VStack spacing={4} align="stretch">
                  {quickActions.map((action) => (
                    <HStack
                      key={action.label}
                      justify="space-between"
                      p={3}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={cardBg}
                    >
                      {/* Left Icon */}
                      <Box bg={action.bg} rounded="full" p={3} display="flex" alignItems="center" justifyContent="center">
                        <span className="text-xl">{action.icon}</span>
                      </Box>

                      {/* Label */}
                      <Text flex={1} ml={4} fontWeight="medium" color="gray.800">
                        {action.label}
                      </Text>

                      {/* Add Button */}
                      <Button
                        onClick={() => navigate(action.path)}
                        size="sm"
                        colorScheme="primary"
                        variant="solid"
                        borderRadius="md"
                        p={0}
                        w="40px"
                        h="40px"
                      >
                        <AddIcon />
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              </CardBody>
            </Card>

            {/* System Alerts */}
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="md" color="gray.800" mb={4}>System Alerts</Heading>
                <VStack spacing={3} align="stretch">
                  {systemAlerts.map((alert: any, index: number) => (
                    <HStack key={index} spacing={3} p={3} bg={alert.color === 'warning' ? 'yellow.50' : 'blue.50'} borderRadius="md">
                      <Icon as={alert.color === 'warning' ? WarningIcon : TimeIcon} color={alert.color === 'warning' ? 'yellow.500' : 'blue.500'} />
                      <Text fontSize="sm" color={alert.color === 'warning' ? 'yellow.800' : 'blue.800'}>
                        {alert.message}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Dashboard;